import React, { useMemo, useState, useCallback } from 'react'
import { Modal, Table, Checkbox, Button, Space, Typography, Alert, Tag } from 'antd'
import { ScissorOutlined } from '@ant-design/icons'

const { Text } = Typography

const itemKey = (item) =>
  item.id != null ? String(item.id) : `mid_${item.menu_item_id ?? item.menuItemId}`

const SplitBillModalNoContext = ({ bill, onClose, onSplitBill }) => {
  const items = useMemo(() => bill?.items || [], [bill])

  console.log("items",items);
  
  const [selectedKeys, setSelectedKeys] = useState([])
  const [splitting, setSplitting] = useState(false)

  const toggleItem = useCallback((key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }, [])

  const getItemAmount = (item) => {
  // 🥤 Handle drinks
  if (Number(item.is_drink) === 1 && item.drink_details) {
    const { pegs = [], peg_qtys = {}, bottle_qty = 0, price_bottle = 0 } = item.drink_details

    // Peg total
    const pegTotal = pegs.reduce((sum, peg) => {
      const qty = Number(peg_qtys?.[peg.id] || 0)
      const price = Number(peg.price || 0)
      return sum + qty * price
    }, 0)

    // Bottle total
    const bottleTotal =
      Number(bottle_qty || 0) * Number(price_bottle || 0)

    return pegTotal + bottleTotal
  }

  // 🍽️ Normal items
  return Number(item.price || 0) * Number(item.qty || 0)
}

  const toggleAll = useCallback(() => {
    const allKeys = items.map(itemKey)
    setSelectedKeys((prev) => (prev.length === allKeys.length ? [] : allKeys))
  }, [items])

  const isAllSelected = selectedKeys.length === items.length && items.length > 0
  const isIndeterminate =
    selectedKeys.length > 0 && selectedKeys.length < items.length

  const selectedItems = useMemo(
    () => items.filter((item) => selectedKeys.includes(itemKey(item))),
    [items, selectedKeys]
  )

  const remainingItems = useMemo(
    () => items.filter((item) => !selectedKeys.includes(itemKey(item))),
    [items, selectedKeys]
  )

const selectedTotal = useMemo(
  () => selectedItems.reduce((sum, i) => sum + getItemAmount(i), 0),
  [selectedItems]
)

const remainingTotal = useMemo(
  () => remainingItems.reduce((sum, i) => sum + getItemAmount(i), 0),
  [remainingItems]
)

const handleSplit = useCallback(async () => {
  const itemIds = selectedItems.map((i) => i.id).filter(Boolean)

  if (itemIds.length === 0) return

  setSplitting(true)
  try {
    const result = await onSplitBill({ item_ids: itemIds })

    // ✅ FIX: close only after success
    if (result !== null && result !== undefined) {
      onClose()
    }

  } catch (error) {
    console.error('Split failed:', error)
  } finally {
    setSplitting(false)
  }
}, [selectedItems, onSplitBill, onClose])
  const columns = [
    {
      title: () => (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={toggleAll}
          disabled={items.length === 0 || splitting}
        />
      ),
      width: 44,
      render: (_, record) => {
        const key = itemKey(record)
        return (
          <Checkbox
            checked={selectedKeys.includes(key)}
            onChange={() => toggleItem(key)}
            disabled={splitting}
          />
        )
      },
    },
   {
  title: 'Item',
  dataIndex: 'name',
  render: (name, record) => (
    <Space size={8}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: (record.is_veg ?? record.veg) ? '#22c55e' : '#ef4444',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      <span>{name || 'Unnamed Item'}</span>

      {Number(record.is_parcel || 0) === 1 && (
        <Tag color="orange" style={{ marginInlineStart: 4 }}>
          Parcel
        </Tag>
      )}
    </Space>
  ),
},
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: 55,
      render: (qty) => <Text>×{qty || 0}</Text>,
    },
    {
      title: 'Amount',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Text strong>
          ₹{getItemAmount(record).toFixed(2)}
        </Text>
      ),
    },
  ]

  return (
    <Modal
      title={
        <Space>
          <ScissorOutlined />
          Split Bill
          {selectedItems.length > 0 && (
            <Tag color="orange">{selectedItems.length} selected</Tag>
          )}
        </Space>
      }
      open={true}
      onCancel={!splitting ? onClose : undefined}
      width={580}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedItems.length === items.length
              ? `All items selected — original bill will be empty`
              : selectedItems.length > 0
              ? `${remainingItems.length} item(s) stay in original (₹${remainingTotal.toFixed(2)})`
              : 'Select items to move to a new bill'}
          </Text>

          <Space>
            <Button onClick={onClose} disabled={splitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<ScissorOutlined />}
              onClick={handleSplit}
              disabled={selectedItems.length === 0}
              loading={splitting}
            >
              Split
              {selectedItems.length > 0
                ? ` ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} — ₹${selectedTotal.toFixed(2)}`
                : ''}
            </Button>
          </Space>
        </Space>
      }
    >
      <Alert
        message="Select items to move to a new bill"
        description="Selected items will be removed from this bill and placed in a new bill."
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      <Table
        dataSource={items}
        columns={columns}
        rowKey={(r) => itemKey(r)}
        pagination={false}
        size="small"
      />
    </Modal>
  )
}

export default SplitBillModalNoContext